import type { ChartRow } from "../types";
import { formatCompactWithUnits, formatWithUnits, unitsFromColumnName } from "./_units";

export function columnIndex(columns: string[], name: string): number {
  const idx = columns.indexOf(name);
  if (idx < 0) {
    throw new Error(`Column "${name}" not in result columns [${columns.join(", ")}]`);
  }
  return idx;
}

export function pluck(rows: ChartRow[], idx: number): Array<string | number | boolean | null> {
  return rows.map((row) => row[idx] ?? null);
}

// Shared typography for ECharts text. Matches the chat UI's
// Inter/system-ui stack so axis labels read as "the same font" as the
// rest of the answer card instead of ECharts' default sans-serif.
const TEXT_FONT_FAMILY =
  '"Inter", "SF Pro Text", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif';
const AXIS_LABEL_COLOR = "#525252"; // neutral-600
const AXIS_LINE_COLOR = "#e5e7eb"; // line tone matching the card border
const SPLIT_LINE_COLOR = "#f1f5f9"; // very light, doesn't compete with bars/lines

export function baseLayout() {
  return {
    grid: { top: 48, left: 56, right: 24, bottom: 56, containLabel: true },
    textStyle: { fontFamily: TEXT_FONT_FAMILY },
    legend: {
      top: 4,
      type: "scroll" as const,
      icon: "circle" as const,
      itemWidth: 8,
      itemHeight: 8,
      textStyle: { fontSize: 12, color: "#404040", fontFamily: TEXT_FONT_FAMILY },
    },
    tooltip: {
      trigger: "axis" as const,
      axisPointer: { type: "shadow" as const },
      textStyle: { fontFamily: TEXT_FONT_FAMILY, fontSize: 12, color: "#171717" },
      borderColor: AXIS_LINE_COLOR,
      backgroundColor: "rgba(255,255,255,0.96)",
      extraCssText: "box-shadow: 0 6px 24px rgba(15,23,42,0.08); border-radius: 10px;",
    },
  };
}

/**
 * Shared axis styling — clean light grid lines + crisp 12px labels
 * with the same font as the rest of the card. Templates spread this
 * into their xAxis / yAxis and add `type`, `data`, etc. on top.
 */
export const axisStyle = {
  axisLabel: {
    color: AXIS_LABEL_COLOR,
    fontFamily: TEXT_FONT_FAMILY,
    fontSize: 12,
    margin: 10,
  },
  axisLine: { lineStyle: { color: AXIS_LINE_COLOR } },
  axisTick: { show: false, lineStyle: { color: AXIS_LINE_COLOR } },
  splitLine: { lineStyle: { color: SPLIT_LINE_COLOR } },
};

/**
 * Compact label formatter for value axes — "1.2M", "350K", "7.5B" —
 * so axis labels stay legible on charts with large counts (revenue,
 * loan portfolios, alert volume) instead of running into each other
 * as "10,000,000". Numbers below 10K format with thousands separators.
 */
export function compactNumber(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  const abs = Math.abs(n);
  if (abs >= 1e9) return trim(n / 1e9) + "B";
  if (abs >= 1e6) return trim(n / 1e6) + "M";
  if (abs >= 1e4) return trim(n / 1e3) + "K";
  return n.toLocaleString();
}

function trim(n: number): string {
  // 1.0 -> "1", 1.5 -> "1.5", 12.34 -> "12.3" (one decimal max)
  if (Math.abs(n) >= 10) return Math.round(n).toString();
  const s = n.toFixed(1);
  return s.endsWith(".0") ? s.slice(0, -2) : s;
}

/** Axis-style preset for numeric (value) axes that may carry big counts.
 *
 * Pass the y-axis column alias (e.g. `total_revenue_amount`) to get a
 * units-aware compact formatter — currency aliases prefix `$`, percent
 * aliases suffix `%`, count aliases drop decimals. Falls back to the
 * plain compact formatter when no column is provided. */
export function valueAxisStyle(yCol?: string) {
  const units = unitsFromColumnName(yCol);
  return {
    ...axisStyle,
    axisLabel: {
      ...axisStyle.axisLabel,
      formatter: units ? (n: number) => formatCompactWithUnits(n, units) : compactNumber,
    },
  };
}

/**
 * Build an ECharts tooltip that formats the x-axis category through
 * `formatXValue` so dates display as "Apr 28, 2026" instead of
 * "2026-04-28" on hover. Series rows render with their humanized name
 * and value as ECharts does by default — we only override the header.
 */
/** Build the axis tooltip. Pass `yCols` (array of value-column aliases) so
 *  per-series numbers format with the right units. The y-series order on
 *  the chart matches `seriesIndex` in the tooltip params; that's the same
 *  order callers pass column names in, so we look up the units by index. */
export function axisTooltip(formatXValue?: (raw: unknown) => string, yCols?: ReadonlyArray<string>) {
  return {
    trigger: "axis" as const,
    axisPointer: { type: "shadow" as const },
    textStyle: { fontFamily: TEXT_FONT_FAMILY, fontSize: 12, color: "#171717" },
    borderColor: "#e5e7eb",
    backgroundColor: "rgba(255,255,255,0.96)",
    extraCssText: "box-shadow: 0 6px 24px rgba(15,23,42,0.08); border-radius: 10px;",
    formatter: (params: unknown) => {
      const arr = Array.isArray(params) ? params : [params];
      if (arr.length === 0) return "";
      const header = formatXValue ? formatXValue((arr[0] as { axisValue: unknown }).axisValue) : String((arr[0] as { axisValue: unknown }).axisValue);
      const rows = arr
        .map((p) => {
          const item = p as { marker: string; seriesName: string; value: number; seriesIndex?: number };
          const colName = yCols && typeof item.seriesIndex === "number" ? yCols[item.seriesIndex] : undefined;
          const units = unitsFromColumnName(colName);
          const formatted = units ? formatWithUnits(item.value, units) : formatNumber(item.value);
          return `<div style="display:flex;align-items:center;gap:8px;padding:1px 0"><span>${item.marker}</span><span style="flex:1;color:#404040">${item.seriesName}</span><b style="margin-left:14px;color:#0f172a">${formatted}</b></div>`;
        })
        .join("");
      return `<div style="font-weight:600;margin-bottom:6px;color:#0f172a">${header}</div>${rows}`;
    },
  };
}

function formatNumber(n: unknown): string {
  if (typeof n !== "number") return String(n ?? "");
  if (!Number.isFinite(n)) return String(n);
  return n.toLocaleString();
}
